import { useState } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

// Service pricing for deposit calculation
const SERVICE_PRICING = {
  'Quick Light Clean (1 hour)': 50,
  'Kitchen Cleaning': 95,
  'Bathroom & Toilet Cleaning': 75,
  'Laundry Room Cleaning': 55,
  'Vacate Clean (Bond Cleaning)': 200,
  'Multiple Areas (2-3 areas)': 150,
  'Airbnb & Short Stay Cleaning': 150,
  'Full House Clean': 180,
  'Full Wet Area Package': 250
};

export const PayPalPaymentButton = ({ bookingData, onSuccess, onError }) => {
  const [loading, setLoading] = useState(false);

  // Calculate 25% deposit
  const basePrice = SERVICE_PRICING[bookingData.service_type] || 100;
  const depositAmount = (basePrice * 0.25).toFixed(2);

  const createOrder = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${BACKEND_URL}/api/payments/create-order`, {
        amount: parseFloat(depositAmount),
        service_type: bookingData.service_type,
        customer_name: bookingData.name,
        customer_email: bookingData.email,
        booking_data: bookingData
      });

      return response.data.order_id;
    } catch (error) {
      console.error('Error creating PayPal order:', error);
      toast.error('Failed to create payment order');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const onApprove = async (data) => {
    try {
      setLoading(true);
      
      // Capture the payment
      const captureResponse = await axios.post(
        `${BACKEND_URL}/api/payments/capture-order/${data.orderID}`
      );

      if (captureResponse.data.status === 'COMPLETED') {
        toast.success('Payment successful!');
        
        // Create the booking after successful payment
        const bookingResponse = await axios.post(`${BACKEND_URL}/api/bookings/`, {
          ...bookingData,
          payment_order_id: data.orderID,
          payment_status: 'completed',
          deposit_paid: parseFloat(depositAmount)
        });

        onSuccess(bookingResponse.data);
      } else {
        toast.error('Payment was not completed');
        onError('Payment not completed');
      }
    } catch (error) {
      console.error('Payment capture error:', error);
      toast.error('Failed to process payment');
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    toast.info('Payment cancelled');
  };

  const onErrorHandler = (err) => {
    console.error('PayPal error:', err);
    toast.error('Payment error occurred');
    onError(err);
  };

  return (
    <div className="mt-6">
      <div className="bg-teal-50 border-2 border-teal-600 rounded-lg p-6 mb-4">
        <h3 className="text-lg font-bold text-teal-900 mb-2">Payment Required</h3>
        <p className="text-gray-700 mb-4">
          A <strong>25% deposit</strong> is required to confirm your booking.
        </p>
        <div className="flex justify-between items-center bg-white rounded p-4">
          <span className="text-gray-600">Deposit Amount:</span>
          <span className="text-2xl font-bold text-teal-600">${depositAmount} AUD</span>
        </div>
        <p className="text-sm text-gray-600 mt-3">
          Remaining balance ({((1 - 0.25) * 100).toFixed(0)}%) will be paid after service completion.
        </p>
      </div>

      <PayPalScriptProvider options={{ 
        "client-id": PAYPAL_CLIENT_ID,
        currency: "AUD"
      }}>
        <PayPalButtons
          style={{ 
            layout: "vertical",
            color: "blue",
            shape: "rect",
            label: "paypal"
          }}
          createOrder={createOrder}
          onApprove={onApprove}
          onCancel={onCancel}
          onError={onErrorHandler}
          disabled={loading}
        />
      </PayPalScriptProvider>

      {loading && (
        <div className="text-center mt-4">
          <p className="text-gray-600">Processing payment...</p>
        </div>
      )}
    </div>
  );
};
